import { Link, useParams } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, Calendar, Clock, Mail, Loader2, Check } from "lucide-react";
import { getPost, BLOG_POSTS } from "../lib/blog";
import { PRODUCTS } from "../lib/data";
import { CTABand } from "../components/site/Bits";
import { Reveal } from "../components/site/Reveal";
import { Seo } from "../components/site/Seo";
import api, { formatApiErrorDetail } from "../lib/api";
import { getVisitorId, getAttribution, trackEvent } from "../lib/track";

function formatDate(iso) {
  return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

function Block({ block }) {
  if (block.type === "h2") return <h2 className="mt-12 mb-4 font-display font-bold text-2xl md:text-3xl text-ink tracking-tight">{block.text}</h2>;
  if (block.type === "ul")
    return (
      <ul className="my-5 space-y-3">
        {block.items.map((it, i) => (
          <li key={i} className="flex gap-3 text-muted-foreground leading-relaxed">
            <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 bg-flame" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    );
  return <p className="my-5 text-muted-foreground text-lg leading-relaxed">{block.text}</p>;
}

function FieldGuidesSignup({ source }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid work email.");
      return;
    }
    setSending(true);
    try {
      await api.post("/subscribe", {
        email, name, source,
        site_url: window.location.origin,
        visitor_id: getVisitorId(), attribution: getAttribution(),
      });
      trackEvent("cta_click", { label: "Field guides subscribe", product: "Blog" });
      setDone(true);
      toast.success("You're subscribed — check your inbox.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="field-guides-signup" className="mt-16 max-w-3xl border border-flame/30 bg-flame/[0.06] p-8 md:p-10">
      {done ? (
        <div className="flex items-start gap-4">
          <div className="h-11 w-11 bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0"><Check className="h-5 w-5" /></div>
          <div>
            <h3 className="font-display font-bold text-xl text-ink">You're on the list.</h3>
            <p className="mt-1.5 text-muted-foreground">We've sent your field guides to <strong>{email}</strong>. New guides and insights will land in your inbox as we publish them.</p>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 text-flame"><Mail className="h-5 w-5" /><span className="kicker">Free Download</span></div>
          <h3 className="mt-3 font-display font-bold text-2xl md:text-3xl text-ink tracking-tight">Get our field guides</h3>
          <p className="mt-2 text-muted-foreground leading-relaxed">Spill response checklists, water-treatment references, and product documentation — the practical stuff your crews actually use. We'll email you the library and send new guides as we publish them.</p>
          <form onSubmit={submit} data-testid="field-guides-form" className="mt-6 flex flex-col sm:flex-row gap-3" noValidate>
            <input
              data-testid="field-guides-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Name (optional)"
              className="w-full sm:w-40 border border-border bg-background px-3.5 py-3 text-ink placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:border-flame focus:ring-flame"
            />
            <input
              data-testid="field-guides-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Work email *"
              className="flex-1 border border-border bg-background px-3.5 py-3 text-ink placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:border-flame focus:ring-flame"
            />
            <button data-testid="field-guides-submit" disabled={sending}
              className="inline-flex items-center justify-center gap-2 bg-flame text-white px-6 py-3 font-semibold hover:bg-flame/90 disabled:opacity-50 transition-colors whitespace-nowrap">
              {sending ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending</> : <>Send me the guides</>}
            </button>
          </form>
          <p className="mt-3 text-xs text-muted-foreground">We'll only use your email to send field guides and occasional insights. Unsubscribe anytime.</p>
        </>
      )}
    </div>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);

  if (!post) {
    return (
      <div className="pt-40 pb-40 text-center container-x">
        <h1 className="font-display font-bold text-3xl text-ink">Article not found</h1>
        <Link to="/blog" className="mt-4 inline-block text-flame font-semibold">Back to the blog</Link>
      </div>
    );
  }

  const absImg = post.heroImage.startsWith("http") ? post.heroImage : `https://oneharvestco.com${post.heroImage}`;
  const canonical = `https://oneharvestco.com/blog/${post.slug}`;
  const relatedProducts = PRODUCTS.filter((p) => (post.related || []).includes(p.slug));
  const morePosts = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.description,
      image: absImg,
      datePublished: post.date,
      dateModified: post.date,
      author: { "@type": "Organization", name: "OneHarvest LLC", url: "https://oneharvestco.com/" },
      publisher: {
        "@type": "Organization",
        name: "OneHarvest LLC",
        logo: { "@type": "ImageObject", url: "https://oneharvestco.com/oneharvest-dark.png" },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
      keywords: post.keywords,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://oneharvestco.com/" },
        { "@type": "ListItem", position: 2, name: "Blog", item: "https://oneharvestco.com/blog" },
        { "@type": "ListItem", position: 3, name: post.title, item: canonical },
      ],
    },
  ];

  return (
    <div data-testid="blog-post-page" className="pt-[72px]">
      <Seo title={post.title} description={post.description} image={post.heroImage} type="article" jsonLd={jsonLd} />

      {/* Hero */}
      <section className="relative">
        <div className="relative h-[46vh] min-h-[340px] w-full overflow-hidden">
          <img src={post.heroImage} alt={post.title} className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
          <div className="absolute inset-0 grain opacity-[0.15]" />
          <div className="relative container-x h-full flex flex-col justify-end pb-12">
            <Reveal>
              <span className="inline-block bg-flame text-white text-xs font-semibold uppercase tracking-wider px-3 py-1">{post.category}</span>
              <h1 className="mt-4 font-display font-extrabold text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-4xl text-balance">{post.title}</h1>
              <div className="mt-5 flex items-center gap-5 text-sm text-white/70">
                <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" />{formatDate(post.date)}</span>
                <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" />{post.readTime}</span>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="bg-background">
        <div className="container-x py-16">
          <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-2 text-muted-foreground hover:text-flame text-sm font-medium mb-8">
            <ArrowLeft className="h-4 w-4" /> All articles
          </Link>
          <article className="max-w-3xl">
            <p className="text-xl text-ink font-medium leading-relaxed border-l-2 border-flame pl-5">{post.excerpt}</p>
            {post.body.map((block, i) => (
              <Block key={i} block={block} />
            ))}
          </article>

          <FieldGuidesSignup source={`Blog: ${post.title}`} />

          {relatedProducts.length > 0 && (
            <div className="mt-16 max-w-3xl border-t border-border pt-10" data-testid="blog-related-products">
              <div className="kicker text-flame mb-5">Related products</div>
              <div className="grid gap-4 sm:grid-cols-2">
                {relatedProducts.map((p) => (
                  <Link key={p.slug} to={`/products/${p.slug}`} className="group flex items-center gap-4 border border-border bg-card p-4 hover:border-flame/60 transition-colors">
                    <img src={p.image} alt={p.name} className="h-16 w-16 object-cover flex-shrink-0" />
                    <div>
                      <div className="kicker text-flame">{p.category}</div>
                      <h3 className="font-display font-bold text-lg text-ink group-hover:text-flame transition-colors">{p.name}</h3>
                    </div>
                    <ArrowUpRight className="ml-auto h-4 w-4 text-muted-foreground group-hover:text-flame transition-colors" />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* More articles */}
      {morePosts.length > 0 && (
        <section className="bg-charcoal">
          <div className="container-x py-16">
            <h2 className="font-display font-bold text-2xl text-white mb-8">More from the blog</h2>
            <div className="grid gap-6 md:grid-cols-2">
              {morePosts.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="group relative flex items-center gap-5 bg-ink border border-white/10 hover:border-flame/50 transition-colors p-4 overflow-hidden">
                  <div className="absolute top-0 left-0 z-20 h-1 w-0 bg-flame group-hover:w-full transition-all duration-500 ease-out" />
                  <img src={p.heroImage} alt={p.title} className="h-20 w-20 object-cover flex-shrink-0 group-hover:scale-105 transition-transform duration-500" />
                  <div>
                    <div className="kicker text-flame">{p.category}</div>
                    <h3 className="mt-1 font-display font-bold text-lg text-white group-hover:text-flame transition-colors leading-snug">{p.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTABand />
    </div>
  );
}
